import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { aplClient } from "../../aplApi/apiClient";
import { ReduxStateAuth } from "../../types/redux/reduxStateAuth";
import { TypeCredForm } from "../../types/TypeCredForm";
import { initialStateAuth } from "../defaultState";
import { Culture } from "../../types/culture";
import { logout, deleteUserSession, loadUserConfig, changePassword } from "../../aplApi/authHelper";
import { getUserConfig } from "../../aplApi/authHelper";
import { getUserSessions } from "../../aplApi/authHelper";
import { getAplError } from "../../aplApi/exceptionHelper";
import { getAplTokens, removeAplTokens, setAplTokens, updateAplTokens } from "../../aplUtils/cookiesWrapper";

export const getUserConfigRedux = createAsyncThunk("auth/getUserConfigRedux", async (userCred: TypeCredForm, thunkApi) => {
    try {
        let response = await getUserConfig(userCred);
        return response.data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const changeUserPasswordRedux = createAsyncThunk(
    "auth/changeUserPasswordRedux",
    async (
        data: {
            currentPassword: string;
            newPassword1: string;
            newPassword2: string;
        },
        thunkApi
    ) => {
        try {
            return (await changePassword(data.currentPassword, data.newPassword1, data.newPassword2, getAplTokens().refreshToken)).data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

export const loadUserConfigRedux = createAsyncThunk("auth/loadUserConfigRedux", async (_, thunkApi) => {
    var tokens = getAplTokens();
    try {
        let response = await loadUserConfig(tokens);
        return response.data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const dropUserConfig = createAsyncThunk("auth/dropUserConfig", async (refreshToken: string, thunkApi) => {
    try {
        let response = await logout(refreshToken);
        return response.data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const setCulture = createAsyncThunk("auth/setCulture", async (culture: Culture, thunkApi) => {
    try {
        let tokenRefreshRequest = getAplTokens();
        let dataUrl = `auth/setculture`;
        let response = await aplClient().put(dataUrl, {
            culture: culture,
            refreshToken: tokenRefreshRequest.refreshToken
        });
        return response.data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const getUserSessionsRedux = createAsyncThunk(`admin/getUserSessionsRedux`, async (_, thunkApi) => {
    try {
        return (await getUserSessions()).data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const deleteUserSessionRedux = createAsyncThunk(`admin/deleteUserSessionRedux`, async (sessionId: string, thunkApi) => {
    try {
        return (await deleteUserSession(sessionId)).data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

const authFeature = createSlice({
    name: "auth",
    initialState: initialStateAuth,
    reducers: {
        setUserConfigRedux: function (state, action) {
            // setTokenCookie(action.payload.token);
            // setRefreshTokenCookie(action.payload.refreshToken);
            return {
                ...state,
                ...action.payload,
                hasToken: true,
                error: null,
                loading: false
            } as ReduxStateAuth;
        },
        setSite: function (state, action) {
            var firstDep = {
                ...state.departments.filter(x => x.siteId === action.payload)
            }[0];
            return {
                ...state,
                sites: [
                    ...state.sites.map(x => ({
                        ...x,
                        selected: action.payload === x.siteId
                    }))
                ],
                departments: [
                    ...state.departments.map(x => ({
                        ...x,
                        selected: firstDep.departmentId === x.departmentId
                    }))
                ]
            } as ReduxStateAuth;
        },
        setDepartment: function (state, action) {
            return {
                ...state,
                departments: [
                    ...state.departments.map(x => ({
                        ...x,
                        selected: action.payload === x.departmentId
                    }))
                ]
            } as ReduxStateAuth;
        },
        setUserSessions: function (state, action) {
            return {
                ...state,
                userSessions: action.payload
            };
        },
        clearSession: () => initialStateAuth
    },
    extraReducers: builder => {
        builder
            .addCase(getUserConfigRedux.pending, state => {
                return {
                    ...state,
                    loading: true
                } as ReduxStateAuth;
            })
            .addCase(getUserConfigRedux.fulfilled, (state, action) => {
                setAplTokens({
                    token: action.payload.token,
                    refreshToken: action.payload.refreshToken,
                    refreshing: false,
                    persist: action.meta.arg.persist
                });
                return {
                    ...state,
                    ...action.payload,
                    error: null,
                    loading: false
                } as ReduxStateAuth;
            })
            .addCase(getUserConfigRedux.rejected, (state, action) => {
                return {
                    ...state,
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAuth;
            });

        builder
            .addCase(changeUserPasswordRedux.pending, state => {
                return {
                    ...state,
                    loading: true
                } as ReduxStateAuth;
            })
            .addCase(changeUserPasswordRedux.fulfilled, (state, action) => {
                let currentTokens = getAplTokens();
                setAplTokens({
                    token: action.payload.token,
                    refreshToken: action.payload.refreshToken,
                    refreshing: false,
                    persist: currentTokens.persist
                });
                return {
                    ...state,
                    ...action.payload,
                    error: null,
                    loading: false
                } as ReduxStateAuth;
            })
            .addCase(changeUserPasswordRedux.rejected, (state, action) => {
                return {
                    ...state,
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAuth;
            });

        builder
            .addCase(loadUserConfigRedux.pending, state => {
                return {
                    ...state,
                    loading: true
                } as ReduxStateAuth;
            })
            .addCase(loadUserConfigRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    ...action.payload,
                    error: null,
                    loading: false
                } as ReduxStateAuth;
            })
            .addCase(loadUserConfigRedux.rejected, (state, action) => {
                console.log(`${new Date().toISOString()} error refreshing token: ${action.error.code} - ${action.payload}`);
                return {
                    ...state,
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAuth;
            });

        builder
            .addCase(setCulture.pending, state => {
                return {
                    ...state,
                    loading: true
                } as ReduxStateAuth;
            })
            .addCase(setCulture.fulfilled, (state, action) => {
                updateAplTokens(action.payload.token, action.payload.refreshToken);
                return {
                    ...state,
                    ...action.payload,
                    loading: false
                } as ReduxStateAuth;
            })
            .addCase(setCulture.rejected, (state, action) => {
                return {
                    ...state,
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAuth;
            });

        builder
            .addCase(dropUserConfig.pending, state => {
                removeAplTokens();
                return initialStateAuth;
            })
            .addCase(dropUserConfig.fulfilled, () => {
                removeAplTokens();
                return initialStateAuth;
            })
            .addCase(dropUserConfig.rejected, () => {
                removeAplTokens();
                return initialStateAuth;
            });

        builder
            .addCase(getUserSessionsRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAuth;
            })
            .addCase(getUserSessionsRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    userSessions: action.payload
                } as ReduxStateAuth;
            })
            .addCase(getUserSessionsRedux.rejected, (state, action) => {
                return {
                    ...state,
                    userSessions: [],
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAuth;
            });

        builder
            .addCase(deleteUserSessionRedux.pending, state => {
                return {
                    ...state,
                    loading: true,
                    error: null
                } as ReduxStateAuth;
            })
            .addCase(deleteUserSessionRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    loading: false,
                    error: null,
                    userSessions: action.payload
                } as ReduxStateAuth;
            })
            .addCase(deleteUserSessionRedux.rejected, (state, action) => {
                return {
                    ...state,
                    userSessions: [],
                    loading: false,
                    error: action.error.code === "ERR_CANCELED" ? null : action.payload
                } as ReduxStateAuth;
            });
    }
});
export const { setUserConfigRedux, setSite, setDepartment, setUserSessions, clearSession } = authFeature.actions;
export default authFeature.reducer;
